import * as Three from 'three';
import { TweenMax, TimelineMax, Power2 as Cubic } from "gsap/all";

export const StateUndefined = 0;
export const StateEnter = 1;
export const StateIdle = 2;
export const StateLeave = 3;

export class Scene {

  camera = null;
  scene = null;
  renderer = null;
  mesh = null;
  zCut = 0.25;

  state = StateUndefined;
  change = false;
  changeImage = "";

  backgroundColor = new Three.Color("#ffffff");
  backImageColor = new Three.Color("#e2e2e2");
  frontImageColor = new Three.Color("#D61800");
  
  updown = 0;

  onresize() {
    let width = window.innerWidth;
    let height = window.innerHeight;
    let aspect = width / height;
    if (this.camera.aspect != aspect) {
      this.camera.aspect = aspect;
      this.camera.updateProjectionMatrix();
      this.rendererdown.setSize(width, height);
      this.rendererup.setSize(width, height);
    }
    this.camera.position.z = Math.max(1.5, Math.min(1/aspect, 2))

    this.materialfront.uniforms.camPos.value = this.camera.position;
    this.materialfront.needsUpdate = true;
    this.materialback.uniforms.camPos.value = this.camera.position;
    this.materialback.needsUpdate = true;
  }

  onscroll() {
    let changed = ((typeof this.rendererdown._lastElScroll === "undefined") || (this.rendererdown._lastBodyScroll !== document.body.scrollTop) || (this.rendererdown._lastElScroll !== document.documentElement.scrollTop));
    if (changed) {
      this.onresize();
      this.rendererdown._lastBodyScroll = document.body.scrollTop;
      this.rendererdown._lastElScroll = document.documentElement.scrollTop;
    }
  }

  animate(time) {
    requestAnimationFrame(this.animate.bind(this));

    this.updateAnimationState();

    for (let i = 0; i < this.objs.length; ++i) {
      let obj = this.objs[i];
      obj.meshfront.position.x = obj.px;
      obj.meshfront.position.y = obj.py;
      obj.meshfront.position.z = obj.pz;

      obj.meshfront.scale.x = obj.sx;
      obj.meshfront.scale.y = obj.sy;

      obj.meshfront.rotation.x = obj.rx;
      obj.meshfront.rotation.y = obj.ry;

      obj.meshback.position.x = obj.px;
      obj.meshback.position.y = obj.py;
      obj.meshback.position.z = obj.pz;

      obj.meshback.scale.x = obj.sx;
      obj.meshback.scale.y = obj.sy;

      obj.meshback.rotation.x = obj.rx;
      obj.meshback.rotation.y = obj.ry;
    }

    let zlimit = this.camera.position.z - this.zCut;

    this.camera.far = 10;
    this.camera.near = zlimit;
    this.camera.updateProjectionMatrix();
    this.rendererdown.render(this.scene, this.camera);

    this.camera.far = zlimit;
    this.camera.near = 0.1;
    this.camera.updateProjectionMatrix();
    this.rendererup.render(this.scene, this.camera);
  }

  changeAnimation(image) {
    this.change = true;
    this.changeImage = image;
  }

  updateAnimationState() {
    if (this.change && (this.state == StateIdle)) {
      this.change = false;
      this.doAnimateLeave();
    }
  }

  doAnimateFirst() {
    this.state = StateEnter;

    let zUp = this.camera.position.z*1.3;
    this.tl = new TimelineMax({ overwrite: true });
    this.tl.staggerFrom(this.objs, 1, {pz: zUp, ease: Cubic.easeOut,
      stagger: {
        grid: [this.dimension, this.dimension],
        from: "start",
        amount: 1
      } }
    )
    .eventCallback("onComplete", () => {
      this.doAnimateIdle();
      this.change = false;
    }, this);
  }

  doAnimateIdle() {
    if (this.tl != null) {
      //this.tl.progress(1);
      this.tl.kill();
    }

    this.state = StateIdle;

    let zBelow = this.zCut/2;
    this.tl = new TimelineMax({ repeat: -1, overwrite: true });
    this.tl.staggerTo(this.objs, 1, {pz: 0.05, ease: Cubic.easeOut, stagger: {
      grid: [this.dimension, this.dimension],
      from: "start",
      amount: 0.3
    }}
    );
    this.tl.staggerTo(this.objs, 1, {pz: 0, ease: Cubic.easeOut, stagger: {
      grid: [this.dimension, this.dimension],
      from: "start",
      amount: 0.3
    }}
    );

  }

  doAnimateEnter() {
    if (this.tl != null) {
      this.tl.kill();
    }

    this.state = StateEnter;

    this.tl = new TimelineMax({ overwrite: true });
    this.tl.staggerTo(this.objs, 0.6, {pz: 0.0, ease: Cubic.easeOut,
      stagger: {
        grid: [this.dimension, this.dimension],
        from: "start",
        amount: 0.6
      },   
    }
    )
    .eventCallback("onComplete", () => {
      this.doAnimateIdle();
    }, this);
  }

  doAnimateLeave() {
    if (this.tl != null) {
      this.tl.kill();
    }

    this.state = StateLeave;

    if (this.updown == 0) {
      this.changeTexture(false, this.changeImage);
      this.updown = 1;
    }
    else {
      this.changeTexture(true, this.changeImage);
      this.updown = 0;
    }

    let zUp = this.camera.position.z*0.4;
    this.tl = new TimelineMax({ overwrite: true });
    this.tl.staggerTo(this.objs, 0.8, {pz: zUp, rx: this.updown*Math.PI, ease: Cubic.easeOut,
      stagger: {
        grid: [this.dimension, this.dimension],
        from: "start",
        amount: 0.8
      },
    }
    )
    /*this.tl.staggerTo(this.objs, 0.4, {rx: this.updown*Math.PI, ease: Cubic.easeOut,
      stagger: {
        grid: [this.dimension, this.dimension],
        from: "start",
        amount: 0.4
      },
    }
    )*/
    .eventCallback("onComplete", () => {
      this.doAnimateEnter();
    }, this);
  }

  changeTexture(front, image) {
    // change one to change all
    let material = (front) ? this.materialfront : this.materialback;
    new Three.TextureLoader().load(require("@/assets/"+image), 
      (texture) => {
        material.uniforms.map.value = texture;
        material.needsUpdate = true;
      });
  }

  changeShaderColor(front, color) {
    let material = (front) ? this.materialfront : this.materialback;
    material.uniforms.colorize.value = color;
    material.needsUpdate = true;
  }

  simpleShader = {
    uniforms: { 
      "map": { value: null },
      "backColor": { value: this.backImageColor },
      "frontColor": { value: this.frontImageColor },
      "camPos": { value: null },
    },
    vertexShader: `
      varying vec2 vUv;
      varying vec3 vPosition;
      void main() {
        vUv = uv;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vPosition = mvPosition.xyz;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `  
      uniform sampler2D map;
      uniform vec3 backColor;
      uniform vec3 frontColor;
      uniform vec3 camPos;
      varying vec2 vUv;
      varying vec3 vPosition;
      void main() {
        vec4 t = texture2D(map, vUv);
        float a = vPosition.z / -camPos.z;
        vec3 m = mix(frontColor, backColor, a);
        gl_FragColor = vec4(m.rgb, t.a);
      }
    `,    
  };

  buildGeometry(scene) {
    this.dimension = 10;
    this.objs = [];

    let materialfront = new Three.ShaderMaterial( {
        uniforms: Three.UniformsUtils.clone(this.simpleShader.uniforms),
        fragmentShader: this.simpleShader.fragmentShader,
        vertexShader: this.simpleShader.vertexShader,
        transparent: true, 
        side: Three.FrontSide
      }
    );

    let materialback = new Three.ShaderMaterial( {
        uniforms: Three.UniformsUtils.clone(this.simpleShader.uniforms),
        fragmentShader: this.simpleShader.fragmentShader,
        vertexShader: this.simpleShader.vertexShader,
        transparent: true, 
        side: Three.FrontSide
      }
    );

    // save
    this.materialfront = materialfront;
    this.materialback = materialback;

    for (let i = 0; i < this.dimension; ++i) {
      for (let j = 0; j < this.dimension; ++j) {
        let d = 1/this.dimension;
        let geometry = new Three.PlaneGeometry(d, d, 1, 1);
        let uvs = geometry.faceVertexUvs[0];
        let x = i*d;
        let y = j*d;
        let w = d;
        let h = d;
        y = (1-d) - y;
        uvs[0][0].set(x, y+h);
        uvs[0][1].set(x, y);
        uvs[0][2].set(x+w, y+h);
        uvs[1][0].set(x, y);
        uvs[1][1].set(x+w, y);
        uvs[1][2].set(x+w, y+h);

        let meshfront = new Three.Mesh(geometry, materialfront);
        scene.add(meshfront);

        let geometryback = geometry.clone();
        geometryback.rotateX(Math.PI);

        let meshback = new Three.Mesh(geometryback, materialback);
        scene.add(meshback);

        let obj = {px: 0, py: 0, pz: 0, sx: 1, sy: 1, rx: 0, ry: 0};
        obj.meshfront = meshfront;
        obj.meshback = meshback;
        obj.px = (i+0.5-this.dimension/2)/this.dimension;
        obj.py = ((this.dimension-j)-this.dimension/2)/this.dimension;
        this.objs.push(obj);
      }
    }
  }

  initialize() {
    let container = document.body;

    this.camera = new Three.PerspectiveCamera(70, 1, 0.01, 1000);
    this.camera.position.z = 1;

    this.scene = new Three.Scene();
    this.buildGeometry(this.scene);
    this.changeTexture(true, "contacts.png");
    this.changeTexture(false, "contacts.png");

    this.rendererdown = new Three.WebGLRenderer({antialias: true});
    this.rendererdown.setClearColor(this.backgroundColor);
    this.rendererdown.domElement.className = "bgfullscreendown";
    container.appendChild(this.rendererdown.domElement);

    this.rendererup = new Three.WebGLRenderer({antialias: true, alpha: true});
    this.rendererup.setClearColor(0x0, 0);
    this.rendererup.domElement.className = "bgfullscreenup";
    container.appendChild(this.rendererup.domElement);

    this.onresize();

    window.addEventListener("resize", this.onresize.bind(this), false);
    //window.addEventListener("scroll", this.onscroll.bind(this), false);

    //this.doAnimate02();
  }

}
